import { Observable, of } from 'rxjs';
import { take, tap, withLatestFrom, concatMap, map } from 'rxjs/operators';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import { ofType } from '@ngrx/effects';

import { CancelShiftUpdate, EndShiftBreak, ReportShiftActionTypes, StartShift, StartTourStopTracking, EndCurrentTourStopTracking } from './actions';
import { getCurrentStop, getCurrentTour, getCurrentShiftBreak, getCurrentTourStopTracking, getRunningShift } from './selectors';
import { Confirmation, ConfType } from '@models/*';
import { Action } from '@ngrx/store';


function debugLog(msg, args) {
    console.info(msg);
}

/**
 * ensures a shift is running (custom RxJS operator)
 * if the shift was not running and cannot be started (also if start shift is cancelled), it will complete the observable
 * and it will execute the onFail callback function if provided
 * allowing for optional error handling, e.g. to dispatch actions)
 */
export function ensureShiftIsRunning(effects, onFail?: (...args: any[]) => void) {
    return function(source$: Observable<any>): Observable<any> {
        return source$.pipe(
            withLatestFrom(
                effects.store.select(getRunningShift),
                effects.store.select(getCurrentShiftBreak),
            ),
            concatMap(([payload, runningShift, currentShiftBreak]: any) => {
                debugLog('[ensureShiftIsRunning] start ensuring shift is running....', payload);
                if (!runningShift) {
                    debugLog('[ensureShiftIsRunning] no shift found, starting new one....', payload);
                    effects.componentGenerator.generateConfirmationDialog(
                        payload.dialogInsertion,
                        ConfType.START_SHIFT,
                    );
                    return effects.confirmations.stream.pipe(
                        take(1),
                        map((conf: Confirmation<any>) => {
                            debugLog(`[ensureShiftIsRunning] Start shift Confirmation clicked ${conf.type}`, payload);
                            if (conf.type === ConfType.CANCEL_CONFIRMATION) {
                                effects.store.dispatch(new CancelShiftUpdate({
                                    date: moment().toISOString(true),
                                }));
                                // this is needed for resetting NgRx state e.g. SubmitProtocol if start shift cancelled => SubmitProtocolFailed
                                // TODO: consider improving semantics and cleaning up this functionality
                                if (onFail) {
                                    debugLog(`[ensureShiftIsRunning] Start Shift cancelled, calling onFail handler`, payload);
                                    onFail(payload);
                                }
                                debugLog('[ensureShiftIsRunning] start shift cancelled', payload);
                            } else if (conf.type === ConfType.START_SHIFT) {
                                effects.store.dispatch(new StartShift({
                                    date: moment().toISOString(true),
                                    nightStay: null,
                                    registeredKmStart: conf.data.registeredKms,
                                }));
                                return true;
                            }
                            return false;
                        }),
                        concatMap((startShiftTriggered: boolean) => {
                            if(!startShiftTriggered) {
                                debugLog('[ensureShiftIsRunning] failed ensuring shift [Shift not started]', payload);
                                return of();
                            }
                            return effects.actions$.pipe(
                                ofType(ReportShiftActionTypes.START_SHIFT_FAIL, ReportShiftActionTypes.START_SHIFT_SUCCESS),
                                take(1),
                                concatMap((action: Action) => {
                                    debugLog(`[ensureShiftIsRunning] Start shift result: ${action.type}`, payload);
                                    if (action.type === ReportShiftActionTypes.START_SHIFT_SUCCESS) {
                                        debugLog('[ensureShiftIsRunning] done ensuring shift....[ShiftStarted]', payload);
                                        return of(payload);
                                    }
                                    if (onFail) {
                                        debugLog(`[ensureShiftIsRunning] Sta rt Shift failed, calling onFail handler`, payload);
                                        onFail(payload);
                                    }
                                    return of();
                                }),
                            );
                        }),
                    );
                } else if (currentShiftBreak) {
                    debugLog(`[ensureShiftIsRunning] Shift break detected, ending it: ${currentShiftBreak}`, payload);
                    const breakPayload = {
                        date: moment().toISOString(true),
                        shiftId: runningShift.id,
                        breakId: currentShiftBreak.id,
                    };
                    effects.store.dispatch(new EndShiftBreak(breakPayload));
                    return effects.actions$.pipe(
                        ofType(ReportShiftActionTypes.END_SHIFT_BREAK_FAIL, ReportShiftActionTypes.END_SHIFT_BREAK_SUCCESS),
                        take(1),
                        concatMap((action: Action) => {
                            debugLog(`[ensureShiftIsRunning] End shiftBreak result:  ${action.type}`, payload);
                            if (action.type === ReportShiftActionTypes.END_SHIFT_BREAK_SUCCESS) {
                                debugLog('[ensureShiftIsRunning] done ensuring shift....[Shift break ended]', payload);
                                return of(payload);
                            }
                            // this is needed for resetting NgRx state e.g. SubmitProtocol if start shift cancelled => SubmitProtocolFailed
                            // TODO: consider improving semantics and cleaning up this functionality
                            if (onFail) {
                                debugLog(`[ensureShiftIsRunning] End shiftBreak failed, calling onFail handler`, payload);
                                onFail(payload);
                            }
                            return of();
                        }),
                    );
                }
                debugLog('[ensureShiftIsRunning] done ensuring shift....', payload);
                return of(payload);
            }),
        );
    };
}

/**
 * ensures the current tour stop is being tracked (custom RxJS operator)
 * if the shift was not running and cannot be started, it will complete the observable
 * and it will execute the onFail callback function if provided
 * allowing for optional error handling, e.g. to dispatch actions)
 */
export function ensureTourStopTrackingStarted(effects, onFail?: (...args: any[]) => void) {
    return function(source$: Observable<any>): Observable<any> {
        return source$.pipe(
            tap( payload  => debugLog('[ensureTourStopTrackingStarted] Start ensuring TourStopTrackingStarted...', payload)),
            ensureShiftIsRunning(effects, onFail),
            tap( payload  => debugLog('[ensureTourStopTrackingStarted] Shift is ensured...', payload)),
            withLatestFrom(
                effects.store.select(getCurrentStop),
                effects.store.select(getCurrentTour),
                effects.store.select(getCurrentTourStopTracking),
            ),
            concatMap(([payload, currentStop, currentTour, currentTourStopTracking]: any) => {
                debugLog(`[ensureTourStopTrackingStarted] Continue ensuring TourStopTrackingStarted...`, payload);
                if (currentTourStopTracking && (
                    !isEqual(currentTourStopTracking.locationGeo, currentStop.locationGeo)
                    || currentTourStopTracking.tourId !== currentStop.tourId
                )) {
                    debugLog(`[ensureTourStopTrackingStarted] Incorrect TourStopTracking detected, ending it...`, payload);
                    effects.store.dispatch(new EndCurrentTourStopTracking());
                    return effects.actions$.pipe(
                        ofType(
                            ReportShiftActionTypes.END_TOUR_STOP_TRACKING_FAIL,
                            ReportShiftActionTypes.END_TOUR_STOP_TRACKING_SUCCESS,
                        ),
                        take(1),
                        concatMap((action: Action) => {
                            debugLog(`[ensureTourStopTrackingStarted] end TourStopTracking result: ${action.type}`, payload);
                            if (action.type === ReportShiftActionTypes.END_TOUR_STOP_TRACKING_SUCCESS) {
                                return of([payload, currentStop, currentTour, currentTourStopTracking]);
                            }
                            if (onFail) {
                                debugLog(`[ensureTourStopTrackingStarted] end TourStopTracking failed, calling onFail handler`, payload);
                                onFail(payload);
                            }
                            return of();
                        }),
                    );
                }
                debugLog(`[ensureTourStopTrackingStarted] No incorrect TourStopTracking detected.`, payload);
                return of([payload, currentStop, currentTour, currentTourStopTracking]);
            }),
            concatMap(([payload, currentStop, currentTour, currentTourStopTracking]: any) => {
                if (!(currentTourStopTracking
                    && isEqual(currentTourStopTracking.locationGeo, currentStop.locationGeo)
                    && currentTourStopTracking.tourId === currentStop.tourId
                )) {
                    debugLog(`[ensureTourStopTrackingStarted] Starting new tour stop tracking....`, payload);
                    effects.store.dispatch(new StartTourStopTracking({
                        date: moment().toISOString(true),
                        tourStop: currentStop,
                        tour: currentTour,
                        dialogInsertion: payload.dialogInsertion,
                    }));
                    return effects.actions$.pipe(
                        ofType(
                            ReportShiftActionTypes.START_TOUR_STOP_TRACKING_FAIL,
                            ReportShiftActionTypes.START_TOUR_STOP_TRACKING_SUCCESS,
                        ),
                        take(1),
                        concatMap((action: Action) => {
                            debugLog(`[ensureTourStopTrackingStarted] Start TourStopTracking result: ${action.type}`, payload);
                            if (action.type === ReportShiftActionTypes.START_TOUR_STOP_TRACKING_SUCCESS) {
                                debugLog('[ensureTourStopTrackingStarted] Done ensuring TourStopTrackingStarted...[TSTStarted]', payload);
                                return of(payload);
                            }
                            if (onFail) {
                                debugLog(`[ensureTourStopTrackingStarted] start TourStopTracking failed, calling onFail handler`, payload);
                                onFail(payload);
                            }
                            return of();
                        }),
                    );
                }

                debugLog('[ensureTourStopTrackingStarted] Done ensuring TourStopTrackingStarted...', payload);
                return of(payload);
            }),
        );
    };
}
